import {
  CellContext,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table as TableType,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useVirtual, VirtualItem } from 'react-virtual';
import Checkbox from '../../Checkbox/Checkbox';
import inputStyles from '../../Input/Input.module.css';
import Tooltip from '../../Tooltip/Tooltip';
import sharedTableStyles from '../SharedTableStyles.module.css';
import SortIcon from '../SortIcon';
import tableStyles from '../TableCard/TableCard.module.css';
import styles from './TableCardWithSelectedTable.module.css';
import { SelectionTablesProps } from './TableCardWithSelectedTable.types';
import { useTranslation } from 'react-i18next';

const TableCardWithSelectedTable = <TData extends object>({
  data = [],
  columns,
  returnedRecordsText,
  enableFilters = false,
  enableMultiRowSelection = true,
  noRowsMessage,
  titleOne,
  titleTwo,
  className,
  onRowSelectionChange,
  selectedRows,
  isLoading,
}: SelectionTablesProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting2, setSorting2] = useState<SortingState>([]);
  const [columnFilters2, setColumnFilters2] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const id = useId();

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, unknown>) =>
      column.id === 'id' ? <div /> : <div className={sharedTableStyles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) => (
      <div className={tableStyles.rowCheckbox}>
        <Checkbox
          label=""
          aria-label="select row"
          id={`${id}-row-${params.row.id}-checkbox`}
          checked={params.row.getIsSelected()}
          onChange={params.row.getToggleSelectedHandler()}
          disabled={!params.row.getCanSelect()}
        />
      </div>
    ),
    [id],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={sharedTableStyles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${id}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [id],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        header: enableMultiRowSelection ? renderHeaderCell : undefined,
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, renderCheckboxCell, renderHeaderCell],
  );

  const tableData = useMemo(() => (isLoading ? Array(30).fill({}) : data), [isLoading, data]);

  const tableColumns = useMemo(() => {
    if (isLoading) {
      return columns.map((column) => ({
        ...column,
        cell: LoadingSkeleton,
      }));
    }
    return columnsWithRowSelection;
  }, [isLoading, columnsWithRowSelection, columns, LoadingSkeleton]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection: true,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
  });

  interface TDataWithOriginalIndex {
    originalIndex: number;
  }

  const handleTableTwoRowSelection = useCallback(
    (row: Row<TDataWithOriginalIndex>) => {
      const newRowSelection = { ...rowSelection };
      delete newRowSelection[row.original.originalIndex];
      table.setRowSelection(newRowSelection);
    },
    [rowSelection, table],
  );

  // modified onChange to deselect the row from table 1 when the checkbox in table 2 is clicked
  const renderCheckboxCellTable2 = useCallback(
    (params: { row: Row<TDataWithOriginalIndex> }) => (
      <div className={sharedTableStyles.rowCheckbox}>
        <Checkbox
          label=""
          aria-label="select row"
          id={`${id}-row-${params.row.id}-checkbox`}
          checked={params.row.getIsSelected()}
          onChange={() => handleTableTwoRowSelection(params.row)}
          disabled={!params.row.getCanSelect()}
        />
      </div>
    ),
    [id, handleTableTwoRowSelection],
  );

  // modified checkbox cell to deselect the row from table 1 when the checkbox in table 2 is clicked
  const columnsWithRowSelection2 = [
    {
      id: 'select',
      header: enableMultiRowSelection ? renderHeaderCell : undefined,
      cell: renderCheckboxCellTable2,
    },
    ...columns,
  ];

  const data2 = useMemo(
    () => Object.keys(rowSelection).map((key) => ({ ...data[+key], originalIndex: key })),
    [data, rowSelection],
  );

  const table2 = useReactTable({
    data: data2,
    columns: columnsWithRowSelection2,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection: true,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters2,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting2,
      columnFilters: columnFilters2,
      // all rows are selected
      rowSelection: data2.reduce((acc, val, index) => {
        acc[index] = true;
        return acc;
      }, {} as { [key: number]: boolean }),
    },
    onSortingChange: setSorting2,
    getSortedRowModel: getSortedRowModel(),
  } as TableOptions<TData>);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtual({
    size: tableData.length,
    parentRef,
  });

  const { rows } = table.getRowModel();
  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;
  const filteredRowsCount = table.getFilteredRowModel().rows.length;
  const filteredRowsCount2 = table2.getFilteredRowModel().rows.length;

  const recordText = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRowsCount);
    }
    return t('common.table.stores', { count: filteredRowsCount });
  }, [returnedRecordsText, filteredRowsCount, t]);

  const recordText2 = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRowsCount2);
    }
    return t('common.table.selected', { count: filteredRowsCount2 });
  }, [returnedRecordsText, filteredRowsCount2, t]);

  return (
    <div className={styles.container}>
      {/* TABLE ONE */}
      <div className={classNames(tableStyles.card, { [className!]: className !== undefined }, styles.tableWidth)}>
        <div
          ref={parentRef}
          className={classNames(tableStyles.tableContainer, {
            [tableStyles.noTitle]: titleOne === undefined,
            [tableStyles.loading]: isLoading,
          })}
        >
          <table className={classNames(tableStyles.table, tableStyles.withRowSelection)}>
            <caption
              className={classNames(tableStyles.tableTitle, { [tableStyles.noTitleCaption]: titleOne === undefined })}
            >
              {titleOne}
            </caption>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      data-testid={`table-header-${header.id}`}
                      key={header.id}
                      className={classNames(sharedTableStyles.columnHeader, {
                        [tableStyles.checkboxColumnHeader]: index === 0,
                      })}
                      aria-sort={getAriaSort(header.column.getIsSorted())}
                    >
                      <div
                        data-testid={`table-sort-button-${header.id}`}
                        className={sharedTableStyles.columnHeaderTextAndIcon}
                        tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                        onClick={
                          header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                        }
                        onTouchStart={
                          header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                        }
                        aria-label={`sort column ${
                          typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                        }`}
                        role="button"
                        onKeyDown={(event: KeyboardEvent) => {
                          switch (event.key) {
                            case ' ':
                            case 'Enter':
                              header.column.toggleSorting();
                              break;
                            default:
                              break;
                          }
                        }}
                      >
                        <span className={sharedTableStyles.columnHeaderText}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {header.column.columnDef.enableSorting && <SortIcon />}
                        {header.column.columnDef.meta?.tooltipText !== undefined && (
                          <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                        )}
                      </div>
                      {header.column.getCanFilter() ? (
                        <input
                          disabled={header.column.columnDef.meta?.disableFilter}
                          onChange={(event) => header.column.setFilterValue(event.target.value)}
                          value={(header.column.getFilterValue() as string) ?? ''}
                          className={classNames(inputStyles.input, sharedTableStyles.headerFilter)}
                          aria-label={`${header.column.columnDef.id} filter input`}
                          id={`${header.column.columnDef.id}filterInput`}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paddingTop > 0 && filteredRowsCount > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {rowVirtualizer.virtualItems.map((virtualRow: VirtualItem) => {
                const { index } = virtualRow;
                const row = rows[index];
                return (
                  <tr
                    key={row?.id}
                    className={classNames(sharedTableStyles.row, {
                      [sharedTableStyles.even]: index % 2 === 0,
                      [sharedTableStyles.selectedRow]: row?.getIsSelected(),
                    })}
                  >
                    {row?.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={sharedTableStyles.cell}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {paddingBottom > 0 && filteredRowsCount > 0 && (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </tbody>
            {!isLoading && filteredRowsCount === 0 && (
              <caption className={tableStyles.noRowsMessage}>{noRowsMessage ?? t('common.table.noData')}</caption>
            )}
          </table>
        </div>
        <div className={styles.footer}>{!isLoading && <h2>{`${filteredRowsCount} ${recordText}`}</h2>}</div>
      </div>
      {/* TABLE TWO */}
      <div className={classNames(tableStyles.card, { [className!]: className !== undefined }, styles.tableWidth)}>
        <div className={classNames(tableStyles.tableContainer, { [tableStyles.noTitle]: titleTwo === undefined })}>
          <table className={classNames(tableStyles.table, tableStyles.withRowSelection)}>
            <caption
              className={classNames(tableStyles.tableTitle, { [tableStyles.noTitleCaption]: titleTwo === undefined })}
            >
              {titleTwo}
            </caption>
            <thead>
              {table2.getHeaderGroups().map((headerGroup) => (
                <tr key={`${headerGroup.id}-2`}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      data-testid={`table-header-${header.id}`}
                      key={`${header.id}-2`}
                      className={classNames(sharedTableStyles.columnHeader, {
                        [tableStyles.checkboxColumnHeaderTableTwo]: index === 0,
                      })}
                      aria-sort={getAriaSort(header.column.getIsSorted())}
                    >
                      <div
                        data-testid={`table-sort-button-${header.id}`}
                        className={sharedTableStyles.columnHeaderTextAndIcon}
                        aria-label={`sort column ${
                          typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                        }`}
                        tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                        onClick={
                          header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                        }
                        onTouchStart={
                          header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                        }
                        role="button"
                        onKeyDown={(event: KeyboardEvent) => {
                          switch (event.key) {
                            case ' ':
                            case 'Enter':
                              header.column.toggleSorting();
                              break;
                            default:
                              break;
                          }
                        }}
                      >
                        <span className={sharedTableStyles.columnHeaderText}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {header.column.columnDef.enableSorting && <SortIcon />}
                        {header.column.columnDef.meta?.tooltipText !== undefined && (
                          <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                        )}
                      </div>
                      {header.column.getCanFilter() ? (
                        <input
                          disabled={header.column.columnDef.meta?.disableFilter}
                          onChange={(event) => header.column.setFilterValue(event.target.value)}
                          value={(header.column.getFilterValue() as string) ?? ''}
                          className={classNames(inputStyles.input, sharedTableStyles.headerFilter)}
                          aria-label={`${header.column.columnDef.id} filter input (second table)`}
                          id={`${header.column.columnDef.id}filterInput2`}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table2.getRowModel().rows.map((row, index) => (
                <tr
                  key={`${row?.id}-2`}
                  className={classNames(sharedTableStyles.row, {
                    [sharedTableStyles.even]: index % 2 === 0,
                    [sharedTableStyles.selectedRow]: row.getIsSelected(),
                  })}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={sharedTableStyles.cell}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {filteredRowsCount2 === 0 && (
              <caption className={tableStyles.noRowsMessage}>{noRowsMessage ?? t('common.table.noData')}</caption>
            )}
          </table>
        </div>
        <div className={styles.footer}>
          <h2>{`${filteredRowsCount2} ${recordText2}`}</h2>
        </div>
      </div>
    </div>
  );
};

export default TableCardWithSelectedTable;
